<script
  setup
  lang="ts"
  generic="T extends Record<string, AcceptableValue>, U extends keyof T"
>
import { useVModel } from '@vueuse/core'
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport,
  type AcceptableValue,
  type SelectItemSelectEvent
} from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { BASE_CLASS, BASE_INVALID_CLASS } from '../Input/variant'

const props = withDefaults(
  defineProps<{
    class?: HTMLAttributes['class']
    modelValue?: AcceptableValue
    options: T[]
    placeholder?: string
    valueKey?: U
    labelKey?: U
    invalid?: boolean
    widthContent?: string
    ableToNextPage?: boolean
    icon?: string
    showReset?: boolean
  }>(),
  {
    class: undefined,
    modelValue: undefined,
    placeholder: 'Pilih Salah Satu',
    valueKey: () => 'id' as U,
    labelKey: () => 'label' as U,
    widthContent: undefined,
    ableToNextPage: false,
    icon: 'material-symbols:keyboard-arrow-down',
    showReset: false
  }
)

const emits = defineEmits<{
  (e: 'update:modelValue', payload?: AcceptableValue): void
}>()

const modelValue = useVModel(props, 'modelValue', emits)
const slots = useSlots()

const openPortal = ref(false)

const resetSelection = (e: SelectItemSelectEvent<AcceptableValue>) => {
  e.preventDefault()
  modelValue.value = undefined
  openPortal.value = false
}

const selectedOption = computed(() =>
  props.options.find((item) => {
    if (!modelValue.value) return false
    if (!item[props.valueKey]) return false

    return (
      item[String(props.valueKey)] ===
      (modelValue.value ? modelValue.value[String(props.valueKey)] : undefined)
    )
  })
)
</script>

<template>
  <SelectRoot v-model="modelValue" v-model:open="openPortal" autocomplete="off">
    <SelectTrigger
      :class="
        cn(
          BASE_CLASS,
          'pr-8 relative data-[placeholder]:text-outline',
          {
            [BASE_INVALID_CLASS]: props.invalid
          },
          props.class
        )
      "
    >
      <SelectValue class="flex gap-2 items-center" :placeholder="placeholder">
        <template
          v-if="slots.default"
          #default="{ selectedLabel, modelValue: selectedValue }"
        >
          <slot
            :selected-label="selectedLabel"
            :selected-value="selectedValue"
            :option="selectedOption"
          >
            <Icon
              v-if="selectedOption?.icon"
              :name="selectedOption.icon.toString()"
              size="20px"
            />
            <span
              class="line-clamp-1 text-left grow"
              :class="{
                'text-outline': !selectedLabel.length
              }"
            >
              {{
                selectedLabel.length
                  ? (selectedOption?.[props.labelKey] ?? selectedLabel)
                  : placeholder
              }}
            </span>
          </slot>
        </template>
      </SelectValue>
      <Icon
        :name="props.icon"
        class="text-primary absolute right-3 top-1/2 -translate-y-1/2"
        size="20px"
      />
    </SelectTrigger>

    <SelectPortal>
      <SelectContent
        class="bg-background rounded-lg shadow-lg z-100 border border-outline-variant p-1.5"
        :class="{ 'w-[var(--reka-select-trigger-width)]': !widthContent }"
        :style="{ width: widthContent }"
        :body-lock="false"
        position="popper"
        :side-offset="4"
      >
        <SelectViewport class="w-full h-full">
          <SelectGroup>
            <SelectItem
              v-if="props.showReset"
              class="text-sm text-on-surface rounded-lg flex items-center justify-between gap-1 px-2.5 py-2 data-[highlighted]:outline-none data-[highlighted]:bg-surface-container data-[disabled]:opacity-60 data-[disabled]:cursor-not-allowed"
              value="all"
              @select="resetSelection"
            >
              <SelectItemText> Semua </SelectItemText>
            </SelectItem>
            <SelectItem
              v-for="(option, index) in props.options"
              :key="`option-${option[props.valueKey]}-${index}`"
              ref="itemRef"
              class="text-sm text-on-surface rounded-lg flex items-center justify-between gap-1 px-2.5 py-2 data-[highlighted]:outline-none data-[highlighted]:bg-surface-container data-[disabled]:opacity-60 data-[disabled]:cursor-not-allowed"
              :value="option[props.valueKey] || '-'"
              :disabled="!!option.disabled"
            >
              <SelectItemText class="flex gap-2 items-center">
                <slot name="option" :option="option">
                  <Icon
                    v-if="option.icon"
                    :name="option.icon.toString()"
                    size="20px"
                  />
                  {{ option[labelKey] }}
                </slot>
              </SelectItemText>
              <SelectItemIndicator class="flex-none w-5 h-5">
                <Icon
                  name="material-symbols:check"
                  class="text-flux-primary"
                  size="20px"
                />
              </SelectItemIndicator>
            </SelectItem>
          </SelectGroup>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
